import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { showBatchaAnalisis } from "../../Action/courseAction";
import { CustomAdminTable, CustomBadge, CustomPageLayout } from "../../Component/customComponents";
import { BatchNames, TableTitle } from "../Common/transactionCommon";

const EMPTY_FILTERS = { startDate: "", endDate: "" };

// Upcoming batches → future dates allowed (no max date, unlike the transaction pages)
const FILTER_FIELDS = [
  { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true },
  {
    key: "endDate",
    label: "End Date",
    type: "date",
    half: true,
    isRequired: true,
    minDate: (v) => v.startDate,
    validate: (value, v) =>
      value && v.startDate && value < v.startDate ? "End date can't be before the start date" : "",
  },
];

const show = (v) => (v === null || v === undefined || v === "" ? "—" : v);
/** "2026-10-20T00:00:00.000Z" → "20/10/2026" (date part only, no time-zone shift) */
const formatDay = (v) => (v ? moment(String(v).slice(0, 10), "YYYY-MM-DD").format("DD/MM/YYYY") : "—");

/** 75 → green, 40–74 → amber, below 40 → red; text values shown as they come */
const PercentBadge = ({ value }) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const tone = n >= 75 ? "success" : n >= 40 ? "warning" : "danger";
  return (
    <CustomBadge tone={tone} size="sm">
      {`${Number(n.toFixed(2))}%`}
    </CustomBadge>
  );
};

const columns = [
  { key: "examType", header: "Exam Name", minWidth: 130, render: (v) => show(v) },
  { key: "baseCourse", header: "Exam Stream", minWidth: 140, render: (v) => <BatchNames names={v} /> },
  { key: "upcomingBatchName", header: "Batch Name", minWidth: 200, wrap: true, render: (v) => <BatchNames names={v} bold /> },
  { key: "batchDate", header: "Batch Date", minWidth: 110, render: (v) => formatDay(v) },
  { key: "admissionTarget", header: "Target", align: "right", render: (v) => show(v) },
  { key: "deficit", header: "Deficit", align: "right", render: (v) => show(v) },
  {
    key: "percentageAchieved",
    header: "Percentage Achieved",
    align: "center",
    minWidth: 150,
    render: (v) => <PercentBadge value={v} />,
  },
];

function BatchaAnalisis() {
  const dispatch = useDispatch();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [batchList, setBatchList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchBatches = (f) => {
    setLoading(true);
    dispatch(
      showBatchaAnalisis(
        { startDate: f.startDate, endDate: f.endDate },
        (res) => {
          setBatchList(Array.isArray(res?.data) ? res.data : []);
          setSearched(true);
          setPage(1);
          setLoading(false);
        },
        () => setLoading(false), // the old page left the spinner running on a failed search
      ),
    );
  };

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return batchList;
    return batchList.filter((row) =>
      [row?.examType, row?.baseCourse, row?.upcomingBatchName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [batchList, searchText]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  return (
    <CustomPageLayout title="UPCOMING BATCH ANALYSIS">
      <CustomAdminTable
        id="batch-analysis-table"
        title={<TableTitle title={`Upcoming Batches (${batchList.length})`} from={filters.startDate} to={filters.endDate} />}
        columns={columns}
        data={pagedRows}
        loading={loading}
        rowKey={(row, index) => row?._id || index}
        enableSerialNumber
        serialHeader="S.No"
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(1);
          },
          placeholder: "Search exam, stream or batch...",
        }}
        advancedFilters={FILTER_FIELDS}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          setFilters(values);
          fetchBatches(values);
        }}
        onResetFilters={() => {
          setFilters(EMPTY_FILTERS);
          setBatchList([]);
          setSearched(false);
        }}
        filterTitle="Batch Date Range"
        filterSubtitle="Choose the start and end date to analyse"
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        emptyTitle={!searched ? "Choose a date range" : searchText ? "No matching batches" : "No upcoming batches"}
        emptyDescription={
          !searched
            ? "Open Filters, pick the start and end date, then Apply."
            : searchText
              ? "Try a different search."
              : "No batches in this date range."
        }
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
}

export default BatchaAnalisis;
